import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';

import { warehouseConfigState } from 'atoms/WarehouseObjState';
import { getWarehouseConfig } from 'repositories/HttpRepository';

export default function useWarehouseConfigLoad(warehouseId) {
  const setWarehouseConfig = useSetRecoilState(warehouseConfigState);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!warehouseId) return undefined;

    const controller = new AbortController();
    const { signal } = controller;
    setConfigLoaded(false);
    setLoadFailed(false);

    getWarehouseConfig(warehouseId, { signal })
      .then((res) => {
        if (signal.aborted) return;
        if (res.ok) {
          setWarehouseConfig(res.data ?? {});
          return;
        }
        // 404 is the backend affirming there is no config document, which is the state the editor
        // exists to fix — so it stays editable, and the atom is cleared in case a previous
        // warehouse left one behind.
        if (res.status === 404) {
          setWarehouseConfig({});
          return;
        }
        // Any other failure must not reach the atom: an empty config is indistinguishable from
        // "this warehouse has none yet", so saving it back would replace the stored document with
        // empty transitions. Callers keep editing disabled off loadFailed instead.
        setLoadFailed(true);
      })
      .catch(() => {
        if (!signal.aborted) setLoadFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setConfigLoaded(true);
      });

    return () => controller.abort();
  }, [warehouseId]);

  return { configLoaded, loadFailed };
}
